import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import WallOfLove from '../components/WallOfLove';
import { 
  Heart, ArrowRight, MapPin, Coffee, Moon, Quote,
  Lightbulb, Building2, Users, Mail, Briefcase, Handshake,
  Shield, Sparkles, Globe, MessageCircleQuestion, Search,
  Linkedin, Twitter, Link2, Check
} from 'lucide-react';
import { trackShareStory, trackFounderDMClick } from '../hooks/useAnalytics';
import { ENGINE_COUNT } from '../constants/product';

const FOUNDER_LINKEDIN_URL = 'https://www.linkedin.com/in/guduru-lakshmi-kiranmai-4a8447178/';

const About = ({ isInternal = false }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = 'https://cartieai.com';
  const shareText = "Why I built CARTIEAI — after watching finance and engineering talk past each other for years. A FinOps operating system that finally speaks both languages. 👇";

  const handleLinkedInShare = () => {
    trackShareStory('linkedin');
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleTwitterShare = () => {
    trackShareStory('x');
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleCopyLink = async () => {
    trackShareStory('copy');
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleFounderDM = () => {
    trackFounderDMClick();
  };

  return (
    <div className={`min-h-screen bg-slate-950 text-slate-100 ${isInternal ? '' : ''}`} data-testid="about-page">
      <SEOHead
        title="About CARTIEAI — Built by Cloud FinOps Engineers"
        description="Built in public by engineers who spent years watching 6-figure cloud bills with no clear answers. CARTIEAI is the FinOps platform that actually takes action."
        canonical="https://cartieai.com/about"
        ogSlug="about"
      />
      {/* Navigation */}
      {!isInternal && (
        <nav className="fixed top-0 w-full z-50 bg-slate-950/90 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-3">
                <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
                <span className="text-xl font-bold">
                  <span className="text-white">CARTIE<span className="text-emerald-600">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
                </span>
              </Link>
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm text-slate-600 hover:text-white">Home</Link>
                <Link to="/about" className="text-sm text-emerald-300">About</Link>
                <Link to="/pricing" className="text-sm text-slate-600 hover:text-white">Pricing</Link>
                <Link to="/contact" className="text-sm text-slate-600 hover:text-white">Contact</Link>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-700 hover:text-white">Sign In</Button>
                </Link>
                <Link to="/login">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">Get Started Free</Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Hero Section */}
      <section className={`${isInternal ? 'pt-8' : 'pt-28'} pb-16 px-4`}>
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 px-4 py-2">
            <Heart className="w-4 h-4 mr-2" />
            Our Story
          </Badge>

          {/* Founder portrait */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl scale-110" aria-hidden />
              <img
                src="/images/founder-formal.jpg"
                alt="Lakshmi Kiranmai Guduru — Founder of CARTIEAI"
                className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full object-cover ring-4 ring-emerald-500/30 shadow-2xl shadow-emerald-500/10"
                loading="eager"
                data-testid="about-hero-founder-photo"
              />
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[11px] font-bold tracking-widest uppercase bg-slate-900 border border-emerald-500/30 text-emerald-700 px-3 py-1 rounded-full shadow-lg">
                Founder
              </span>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 text-white tracking-tight">
            From a Simple Conversation<br />
            <span className="text-emerald-400">
              to a Dream Come True
            </span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            This is not just a product. It is a two-year journey of late nights, hard questions, quiet doubt — and the stubborn belief that the tool I wished existed was worth building.
          </p>
          <p className="text-sm text-slate-400 mt-4">
            — Lakshmi Kiranmai Guduru, Founder of CARTIEAI
          </p>
        </div>
      </section>

      {/* Where My Story Begins */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-emerald-300" />
            <h2 className="text-2xl font-bold">Where My Story Begins</h2>
          </div>
          <p className="text-lg text-slate-300 mb-4">
            I was born in <span className="text-emerald-300 font-semibold">India</span> — a land of dreamers and believers.
          </p>
          <p className="text-slate-400">
            Growing up, I was always curious about technology and how it could solve real problems. That curiosity brought me across the ocean to pursue my dreams.
          </p>
        </div>
      </section>

      {/* Chapter 1 */}
      <section className="py-12 px-4 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-slate-800 text-slate-300">Chapter 1</Badge>
          <h2 className="text-3xl font-bold mb-6">Wichita — Where the seed was planted</h2>
          
          <p className="text-slate-300 mb-6">
            It was during my Master's at <span className="text-emerald-300">Wichita State University</span> when this idea first sparked. I was surrounded by brilliant minds, learning the tech skills that are actually shaping the future — <span className="text-white font-medium">AI, multi-cloud environments, IoT, Python, R, and LLM integrations.</span> And I kept noticing something that bothered me — <span className="text-white font-medium">companies were losing so much money on cloud resources they did not even know they were paying for.</span>
          </p>
          <p className="text-slate-300 mb-6">
            The more I studied what <span className="text-white font-medium">real technological innovation</span> actually meant, the more I could clearly see pain points that financial operations teams were quietly struggling with — teams searching for a tool, <span className="text-emerald-300">any tool</span>, that could genuinely make their work easier.
          </p>

          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6 my-8">
            <h3 className="text-xl font-semibold text-emerald-300 mb-4">A Chance Encounter</h3>
            <p className="text-slate-300 mb-4">
              One day at a conference hall in downtown Wichita, I shared this idea with a complete stranger — a kind lady named <span className="text-white font-medium">Clara Anderson</span>. I was nervous, not sure if my idea made any sense. She listened patiently, smiled, and said:
            </p>
            <div className="flex gap-3 bg-white/5 rounded-xl p-4">
              <Quote className="w-6 h-6 text-emerald-300 flex-shrink-0" />
              <p className="text-white italic text-lg">"That's a good idea. You should build it."</p>
            </div>
            <p className="text-slate-400 mt-4">
              Clara was just a random person I met that day. But her words stayed with me forever. <span className="text-emerald-300">Sometimes all we need is one person to believe in us.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Chapter 1.5 — Asking the hard questions */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-slate-800 text-slate-300">Chapter 1.5</Badge>
          <h2 className="text-3xl font-bold mb-6">Asking the questions nobody wanted to answer</h2>

          <p className="text-slate-300 mb-6">
            An idea is easy. <span className="text-white font-medium">Validating it is the hard part.</span> So I started asking open questions to everyone I believed could give me an honest answer — engineers, finance folks, cloud architects, anyone who had ever looked at a cloud bill and sighed.
          </p>

          <div className="grid md:grid-cols-3 gap-4 my-8">
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/10">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mb-3">
                <MessageCircleQuestion className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
              </div>
              <p className="text-sm text-slate-300 font-medium mb-1">Some gave crazy answers.</p>
              <p className="text-xs text-slate-400">Ideas that were wild, creative, impossible — but they made me think.</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/10">
              <div className="w-11 h-11 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center mb-3">
                <Lightbulb className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
              </div>
              <p className="text-sm text-slate-300 font-medium mb-1">Some shared what they knew.</p>
              <p className="text-xs text-slate-400">Partial answers, patterns from their own teams, honest frustrations.</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/10">
              <div className="w-11 h-11 rounded-xl bg-rose-500 shadow-lg shadow-rose-500/40 flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
              </div>
              <p className="text-sm text-slate-300 font-medium mb-1">Some ignored me.</p>
              <p className="text-xs text-slate-400">Cold replies, dismissals — and that taught me just as much.</p>
            </div>
          </div>

          <p className="text-slate-300 mb-6">
            Every conversation — the good, the awkward, and the cold ones — pointed at the same silent truth: <span className="text-white font-medium">the pain was real, but nobody had figured out how to solve it end-to-end.</span>
          </p>

          {/* Competitive awakening — "they exist, but they are not enough" */}
          <div className="bg-gradient-to-br from-emerald-500/5 via-emerald-400/5 to-pink-500/5 border border-emerald-500/20 rounded-2xl p-6 my-8">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <Search className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">"But what about Vantage? CloudHealth? They already exist."</h3>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Yes — I knew about them. Before I built a single feature, I sat down with every name in the FinOps space — <span className="text-white font-medium">Vantage, CloudHealth, Apptio Cloudability, Spot.io, Kion, ProsperOps</span> — and studied them. I read their docs, watched their demos, tried their free trials, talked to engineers who had used them in production.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">
                  They are not bad products. For a Fortune-500 with a dedicated FinOps team and a six-figure budget, they work. <span className="text-white font-medium">But the companies I had been working with were not Fortune 500</span>. They were 15-person scale-ups, single-cloud-engineer shops, and finance leads who did not even have a "FinOps" title yet. For them, the existing tools were:
                </p>
                <ul className="text-slate-300 leading-relaxed space-y-2 mb-4 ml-1">
                  <li>📊 <span className="text-white font-medium">Built for the FinOps practitioner</span> — not the engineer who actually has to fix the cost.</li>
                  <li>🌉 <span className="text-white font-medium">Showing data, not bridging teams</span> — finance and engineering still could not see the same view.</li>
                  <li>🤖 <span className="text-white font-medium">"AI" as a marketing checkbox</span> — bolted-on chat boxes that could not answer a real grounded question.</li>
                  <li>💸 <span className="text-white font-medium">Priced out of reach</span> — the people bleeding the most could not afford the tools that would help them.</li>
                </ul>
                <p className="text-slate-300 leading-relaxed">
                  That was the awakening. <span className="text-emerald-300 font-semibold">The category was full, but the gap was specific, structural, and large.</span> I made one promise to myself: I would build a system where the engineer and the finance lead see the same thing, talk in the same vocabulary, and act on the same data — together. <span className="text-emerald-300 font-medium">That is what CARTIEAI is.</span>
                </p>
                <p className="text-xs text-slate-400 leading-relaxed mt-3 pt-3 border-t border-emerald-500/15">
                  💼 <span className="text-emerald-300/90 font-medium">For enterprise readers:</span> CARTIEAI was built mid-market first <em>by design</em> — but the architecture (multi-tenant isolation, SOC 2 prep pack, role-based access, MFA, audit log with PDF export, SSO-ready) is enterprise-grade from day one. We <em>love</em> serving enterprise customers — we simply made sure mid-market teams could come along too.
                </p>
              </div>
            </div>
          </div>

          {/* The Gold — what we have that competitors don't */}
          <div className="my-12">
            <div className="text-center mb-8">
              <Badge className="mb-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-300 border-amber-400/30 px-3 py-1">The Gold</Badge>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">
                What we built that the others{' '}
                <span className="text-amber-300">don't have</span>
              </h3>
              <p className="text-sm text-slate-400">Seven design choices that sit at the heart of CARTIEAI — and not in a single competitor we studied.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* 1 */}
              <div className="bg-gradient-to-br from-emerald-500/[0.07] to-teal-500/[0.04] border border-emerald-500/20 rounded-2xl p-5 hover:border-emerald-400/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center text-lg">🧠</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">#1</p>
                    <h4 className="text-base font-bold text-white">An AI advisor grounded in <em>your</em> data</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Claude Sonnet 4.5 + GPT-5.2 + Gemini 3 multi-LLM routing with deterministic failover, every answer cited to a specific warehouse, hour, and dollar amount. <span className="text-emerald-300">No bolted-on chat box.</span>
                </p>
              </div>

              {/* 2 */}
              <div className="bg-gradient-to-br from-emerald-500/[0.07] to-teal-500/[0.04] border border-emerald-500/20 rounded-2xl p-5 hover:border-emerald-400/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center text-lg">💬</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">#2</p>
                    <h4 className="text-base font-bold text-white">{ENGINE_COUNT} Smart Engines named after real human questions</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Spike Autopsy. Invoice Autopsy. SaaS Detective. Zombie Killer. Egress Shield. Cost Bot. <span className="text-emerald-300">Every name is a quote heard in a real meeting</span> — not a SKU, not a product line.
                </p>
              </div>

              {/* 3 */}
              <div className="bg-gradient-to-br from-emerald-500/[0.07] to-teal-500/[0.04] border border-emerald-500/20 rounded-2xl p-5 hover:border-emerald-400/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center text-lg">🌉</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">#3</p>
                    <h4 className="text-base font-bold text-white">One language for finance + engineering</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  The same view, the same number, the same drill-down — readable by an engineer in code-review terms <em>and</em> by finance in quarterly-forecast terms. <span className="text-emerald-300">The bridge nobody else built.</span>
                </p>
              </div>

              {/* 4 */}
              <div className="bg-gradient-to-br from-amber-500/[0.07] to-orange-500/[0.04] border border-amber-500/20 rounded-2xl p-5 hover:border-amber-400/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center text-lg">⚡</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-300/80">#4</p>
                    <h4 className="text-base font-bold text-white">Action Center, not just a dashboard</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Spend Freeze, auto-tag the untagged 50 %, kill zombies on a click, ship Slack alerts with the fix attached. <span className="text-amber-300">CARTIE does the work — it doesn't just describe it.</span>
                </p>
              </div>

              {/* 5 */}
              <div className="bg-gradient-to-br from-rose-500/[0.07] to-pink-500/[0.04] border border-rose-500/20 rounded-2xl p-5 hover:border-rose-400/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-rose-500 shadow-lg shadow-rose-500/40 flex items-center justify-center text-lg">🔒</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-300/80">#5</p>
                    <h4 className="text-base font-bold text-white">Stateless credential architecture</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Your cloud keys live in memory for the duration of the audit and disappear when the request ends. <span className="text-rose-300">We never persist them — not on disk, not in the database, not anywhere.</span> Most competitors store them.
                </p>
              </div>

              {/* 6 */}
              <div className="bg-gradient-to-br from-emerald-500/[0.07] to-teal-500/[0.04] border border-indigo-500/20 rounded-2xl p-5 hover:border-indigo-400/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center text-lg">☁️</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">#6</p>
                    <h4 className="text-base font-bold text-white">6 LIVE clouds — including the data warehouses</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  AWS · Azure · GCP · <span className="text-emerald-300 font-medium">Snowflake</span> · <span className="text-emerald-300 font-medium">Databricks</span> · DigitalOcean — all read-only, all real-time. The data clouds are usually where the bleeding actually happens, and where most FinOps tools stop.
                </p>
              </div>

              {/* 7 — full width */}
              <div className="md:col-span-2 bg-gradient-to-br from-emerald-500/[0.08] via-amber-500/[0.06] to-teal-500/[0.05] border border-amber-500/30 rounded-2xl p-5 hover:border-amber-400/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center text-lg">🎁</div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-300/80">#7 — the part that surprises everyone</p>
                    <h4 className="text-base font-bold text-white">Free public tools, zero signup</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Run a <Link to="/snowflake-health-score" className="text-amber-300 underline decoration-dotted hover:text-amber-200">Snowflake Health Score</Link> right now without typing your email. Read our cornerstone playbooks for free, with downloadable PDF audit checklists. <span className="text-amber-200">We earn your trust before we ask for your card.</span> Most of the category gates everything behind a sales call.
                </p>
              </div>
            </div>

            <p className="text-center text-xs text-slate-400 mt-6">
              These seven design choices took two years to build. They are not features that can be retrofitted onto an existing dashboard — they are <span className="text-amber-300/80">how the system was conceived.</span>
            </p>
          </div>

          {/* One Pair of Hands — minimal solo-builder credit */}
          <div className="my-12 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-rose-500/[0.04] via-emerald-400/[0.04] to-amber-500/[0.04] rounded-3xl blur-xl" />
            <div className="relative bg-gradient-to-br from-slate-900/80 to-slate-950/60 border border-rose-400/20 rounded-3xl p-8 backdrop-blur-sm text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-400 bg-gradient-to-br from-rose-400 via-pink-500 to-teal-500 shadow-lg shadow-rose-500/30 mb-4">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-300/80 mb-2">One pair of hands</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Built end-to-end. <span className="text-emerald-400">By me. Alone.</span>
              </h3>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border border-emerald-500/20 rounded-2xl p-6 my-8">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <Search className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">So I did the research myself.</h3>
                <p className="text-slate-300 leading-relaxed">
                  I dug into every pain point personally — late nights reading post-mortems, studying cloud bills, tracing spikes, understanding why finance and engineering kept talking past each other. I wanted to <span className="text-emerald-300 font-semibold">feel</span> the problem, not just read about it. That research became the foundation of every feature inside CARTIEAI today.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 2 */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-slate-800 text-slate-300">Chapter 2</Badge>
          <h2 className="text-3xl font-bold mb-6">Texas — Where the dream took shape</h2>
          
          <p className="text-slate-300 mb-6">
            After completing my Master's, I moved to Texas. Life got real — I was job hunting, adapting to a new city, and trying to figure out my future.
          </p>
          <p className="text-slate-300 mb-6">
            But the idea never left me. Every night after work, every weekend, I would sit down and work on it. I did not have a grand plan or a roadmap. I just built what came to my mind — writing features on paper without second thought, validating them with myself, using Google as my mentor to refine and filter everything.
          </p>

          <div className="grid grid-cols-2 gap-6 my-8">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-emerald-500/30 transition-colors text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center shadow-md shadow-emerald-500/20">
                <Moon className="w-5 h-5 text-emerald-300" />
              </div>
              <div className="text-3xl font-bold text-white">2 Years</div>
              <div className="text-slate-400 text-sm">of late nights and weekends</div>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-amber-500/30 transition-colors text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-amber-500/30 to-orange-500/15 border border-amber-400/40 flex items-center justify-center shadow-md shadow-amber-500/20">
                <Coffee className="w-5 h-5 text-amber-300" />
              </div>
              <div className="text-3xl font-bold text-white">Countless cups</div>
              <div className="text-slate-400 text-sm">of coffee and determination</div>
            </div>
          </div>

          <p className="text-slate-300">
            I started with basic Python and React. Then slowly added more tech stacks, integrated AI/ML models, and piece by piece, <span className="text-emerald-300 font-semibold">CARTIEAI came to life.</span>
          </p>
          <p className="text-slate-400 mt-4 italic">
            Our roots started in Wichita and found their home in Texas — two places that will always be part of our story.
          </p>
        </div>
      </section>

      {/* Chapter 2.5 */}
      <section className="py-12 px-4 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-slate-800 text-slate-300">Chapter 2.5</Badge>
          <h2 className="text-3xl font-bold mb-6">The Moment Everything Clicked</h2>
          
          <p className="text-slate-300 mb-6">
            After moving to Texas, I joined <span className="text-emerald-300">a major healthcare company</span> as a Software Engineer. Little did I know, this job would validate everything I had been building.
          </p>

          <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6 my-8">
            <h3 className="text-xl font-semibold text-red-400 mb-4">The Pain I Witnessed Every Week</h3>
            <p className="text-slate-300 mb-4">
              Every month, the same story repeated. The Snowflake bill comes in. Numbers do not match. The dashboard shows one thing, our side shows another. We would try to figure out where the mismatch happened — sometimes jumping on calls with Snowflake to query cost per service at warehouse and user level. <span className="text-red-400">The team still took weeks to figure out why.</span>
            </p>
            <ul className="space-y-3 text-slate-400">
              <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span>Emergency meetings called to investigate</span></li>
              <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span>Which warehouse caused the spike?</span></li>
              <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span>Which services had the huge spike?</span></li>
              <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span>Who ran that expensive query?</span></li>
              <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span>Which service is using more credits?</span></li>
              <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span className="text-white font-medium">Weeks to find answers. Month-long huge audits.</span></li>
            </ul>
          </div>

          <p className="text-slate-300 mb-4">
            I sat in those meetings. I saw the frustration. I heard the same questions repeated week after week:
          </p>
          
          <div className="bg-white/5 rounded-xl p-6 my-6 space-y-3">
            <p className="text-white italic">"Why did our costs spike?"</p>
            <p className="text-white italic">"How much did that particular warehouse consume?"</p>
            <p className="text-white italic">"Where can we reduce costs? How can we make it cost-effective?"</p>
          </div>

          <p className="text-slate-300 mb-6">
            And the answers took weeks — queries, calls, finger-pointing between finance and engineering. I realized the exact same pattern was playing out everywhere: <span className="text-white font-medium">AWS, Azure, GCP, Databricks, SaaS contracts, GPU clusters, data egress</span>. The cloud had changed, but the pain was identical. <span className="text-emerald-300 font-semibold">I knew I could solve it — not just for Snowflake, but for all of it.</span>
          </p>

          <p className="text-slate-300 mb-4">
            That became the seed for <span className="text-white font-medium">CARTIE<span className="text-emerald-300">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>. What started as solving Snowflake cost mysteries has grown into a full FinOps operating system across <span className="text-emerald-300 font-semibold">15 cloud and SaaS platforms</span>, with <span className="text-emerald-300 font-semibold">13 purpose-built features</span> — each one answering a specific question I personally heard asked in those rooms:
          </p>

          <div className="grid sm:grid-cols-2 gap-3 my-6">
            {[
              { name: 'Invoice Autopsy', pain: '"Why did our bill spike?"', path: '/invoice-autopsy' },
              { name: 'Cost Scorecard', pain: '"Who owns this cost?"', path: '/bill-detective' },
              { name: 'SaaS Detective', pain: '"Where is money hiding?"', path: '/saas-detective' },
              { name: 'Zombie Killer', pain: '"What can we shut off safely?"', path: '/zombie-killer' },
              { name: 'Smart Tag Fixer', pain: '"Why is nothing tagged?"', path: '/smart-tags' },
              { name: 'AI/GPU Intelligence', pain: '"What is our AI really costing us?"', path: '/ai-gpu' },
              { name: 'Egress Shield', pain: '"Where are these transfer fees from?"', path: '/egress-shield' },
              { name: 'Commitment Intel', pain: '"Are we using our RIs/SPs right?"', path: '/commitment-intel' },
              { name: 'Vendor Deal Intel', pain: '"Are we overpaying on contracts?"', path: '/vendor-deals' },
              { name: 'Compliance Mapper', pain: '"Can we pass this audit?"', path: '/compliance-mapper' },
              { name: 'Board Deck Generator', pain: '"Can I have a summary by tomorrow?"', path: '/board-deck' },
              { name: 'Carbon + ESG', pain: '"What about our sustainability numbers?"', path: '/carbon' },
            ].map((f) => (
              <Link
                key={f.name}
                to={f.path}
                className="group bg-white/[0.03] hover:bg-white/[0.06] rounded-lg p-3 border border-white/10 hover:border-emerald-500/40 flex items-center justify-between gap-3 transition-all hover:-translate-y-0.5"
                data-testid={`about-feature-link-${f.path.replace('/', '')}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-700 truncate group-hover:text-emerald-700 transition-colors">{f.name}</p>
                  <p className="text-xs text-slate-400 truncate">{f.pain}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </Link>
            ))}
          </div>

          <p className="text-emerald-300 font-medium">
            Every feature born from real pain I witnessed firsthand.
          </p>
        </div>
      </section>

      {/* Chapter 2.5 — The Token Story (founder origin: blockchain tokens → AI tokens) */}
      <section className="py-12 px-4 bg-gradient-to-b from-transparent via-emerald-500/[0.03] to-transparent" data-testid="token-story-section">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-emerald-500/10 text-emerald-700 border-emerald-500/20">Chapter 2.5 — The Token Story</Badge>
          <h2 className="text-3xl font-bold mb-6">Tokens twice — first crypto, then AI</h2>

          <p className="text-slate-300 mb-5 leading-relaxed">
            The word <span className="text-white font-medium">"tokens"</span> entered my life during my bachelor's <span className="text-emerald-300 font-semibold">(2016–2020)</span>. While my classmates were memorising syllabi, I was reading blockchain whitepapers online and picking up books on the subject from the library — trying to understand how a digital token could carry value and be auditable at the same time.
          </p>

          <p className="text-slate-300 mb-5 leading-relaxed">
            By <span className="text-emerald-300 font-semibold">2020</span>, I had wrapped my final-year project — <span className="text-white italic font-semibold">"Developing a Decentralized Application to Demonstrate the Usage of Cryptocurrency through Ethereum Blockchain"</span> — where I built a custom cryptocurrency, wrote smart contracts in Solidity, stood up the server and web app, and learned how token movement is logged on-chain. Alongside it I shipped mini projects like <span className="text-white font-semibold">Test-Driven Development of a Dapp</span>, <span className="text-white font-semibold">smart-contract design</span>, and an <span className="text-white font-semibold">Ethereum private blockchain</span>.
          </p>

          <p className="text-slate-300 mb-5 leading-relaxed">
            My guide on that journey was <span className="text-emerald-300 font-semibold">Dr. Sai Manoj Kudaravalli</span>. He was a CEO at the time — busy as you'd expect — yet the door was always open. I could walk up and ask him anything without hesitation, and he'd point me in the right direction. He didn't have hours to <em>teach</em> in the traditional sense; he <em>guided</em>, and that was enough. Back then it was just a university project — neither of us treated it like a thesis on the future. Years later, that quiet guidance turned out to be the foundation everything else was built on.
          </p>

          <div className="bg-white/5 rounded-xl p-6 my-6 border-l-4 border-emerald-500/60">
            <p className="text-white italic leading-relaxed">
              "Back then, tokens were a curiosity. A decade later, AI gave us a new kind of token — and nobody was tracking those."
            </p>
          </div>

          <p className="text-slate-300 mb-5 leading-relaxed">
            I came back to the classroom for my master's — <span className="text-emerald-300 font-semibold">Spring 2022 through Fall 2023</span>. Then through <span className="text-emerald-300 font-semibold">2024</span> — somewhere between job applications, coffee, and the occasional existential crisis 😄 — I started using LLMs the way everyone did: for code, summaries, side projects. And then I noticed something strange. Cloud bills had per-second granularity. SaaS bills had per-seat granularity. AI bills had <span className="text-white font-medium">input/output tokens</span> — billed at four-decimal precision per million — and most teams had <em>zero visibility</em> into where their token spend was going.
          </p>

          <p className="text-slate-300 mb-5 leading-relaxed">
            It started as a joke between me and my friend <span className="text-emerald-300 font-semibold">Vishwanath Anandh</span>. "Wouldn't it be funny if your chat feature was losing money on every paying customer?" We laughed. Then I went and ran the math on a real workload. <span className="text-white font-medium">It wasn't funny anymore.</span>
          </p>

          <p className="text-slate-300 mb-5 leading-relaxed">
            I took the joke seriously. I read papers, talked to people using paid AI models — folks who kept hitting the same wall, where every next task asked them to pay extra on top of their subscription — and confirmed the pain in conversation after conversation. I sketched the first wireframes on <span className="text-white font-semibold">paper stubs</span>, kept them aside for weeks, came back to them, refined, and finally started building — line by line.
          </p>

          <p className="text-slate-300 mb-5 leading-relaxed">
            The validation came at a networking meetup. A stranger I'd never met independently described the same pain. That was the moment I decided to build this seriously — not as a side project, but as the work I want to do for years.
          </p>

          <p className="text-slate-300 mb-2 leading-relaxed">
            That's why CARTIEAI has a dedicated <Link to="/token-intelligence-suite" className="text-emerald-700 hover:text-emerald-700 font-medium underline decoration-emerald-500/30 hover:decoration-emerald-400">Token Intelligence Suite</Link> — 10 features that bring blockchain-grade token observability to the AI era. Same word, same instinct, different decade.
          </p>

          <p className="text-emerald-300 font-medium italic">
            The cycle came full circle: from tracking ETH tokens for a class project, to tracking AI tokens for a thousand companies.
          </p>
        </div>
      </section>

      {/* Chapter 3 - Gratitude */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-slate-800 text-slate-300">Chapter 3</Badge>
          <h2 className="text-3xl font-bold mb-6">A Heart Full of Gratitude</h2>
          
          <p className="text-slate-300 mb-8">
            None of this would have been possible without the people who supported me — morally and emotionally.
          </p>

          <div className="space-y-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <p className="text-slate-300">
                <span className="text-emerald-300 font-semibold">To my parents</span> — who believed in me when I did not believe in myself. Who supported me through every difficult moment, every doubt, every failure. Your love gave me strength to keep going.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <p className="text-slate-300">
                <span className="text-emerald-300 font-semibold">To my relatives and family</span> — who stood by me, encouraged me, and never let me feel alone in this journey. Your moral support meant everything.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <p className="text-slate-300">
                <span className="text-emerald-300 font-semibold">To my friends</span> — who listened to my endless ideas, celebrated my small wins, and picked me up when I was down. You made this journey bearable.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <p className="text-slate-300">
                <span className="text-emerald-300 font-semibold">To Dr. Sai Manoj Kudaravalli</span> — my undergraduate project guide. You were a CEO at the time, but the door was always open. I could ask anything without hesitation. You didn't have time to teach — you guided me, and that was exactly what I needed. Back then it was "just a university project." Today it's the foundation everything else stands on. Thank you.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <p className="text-slate-300">
                <span className="text-emerald-300 font-semibold">To Vishwanath Anandh</span> — the friend who started this with a joke. "Wouldn't it be funny if your chat feature was losing money on every paying customer?" Turns out it wasn't funny. It was a real problem. Thank you for the late-night conversation that started everything.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <p className="text-slate-300">
                <span className="text-emerald-300 font-semibold">To Clara Anderson</span> — a stranger who took a moment to listen and encourage. You probably do not remember me, but I will never forget you.
              </p>
            </div>
          </div>

          <div className="text-center my-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-rose-500/30 to-pink-500/15 border border-rose-400/40 flex items-center justify-center shadow-lg shadow-rose-500/30">
              <Heart className="w-7 h-7 text-rose-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Thank You.</h3>
            <p className="text-slate-400">From the bottom of my heart.</p>
            <p className="text-emerald-300 mt-2">Every single one of you made this possible.</p>
          </div>
        </div>
      </section>

      {/* Special Thanks to Doubters */}
      <section className="py-12 px-4 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-xl font-semibold text-slate-400 mb-4">A Special Thanks</h3>
          <h2 className="text-2xl font-bold text-white mb-6">To those who doubted</h2>
          
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
            <p className="text-lg text-slate-300 italic">
              "Special thanks to everyone who said I can't make it."
            </p>
          </div>
          
          <p className="text-slate-400">
            Your doubt gave me the courage to build something — whether small or big, it does not matter. What matters is that I tried, I built, and I did not give up.
          </p>
          <p className="text-emerald-300 mt-4">
            Every "no" became fuel. Every doubt became motivation. Thank you for making me stronger.
          </p>
        </div>
      </section>

      {/* A Small Grain */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Globe className="w-7 h-7 text-emerald-300" />
          </div>
          <h2 className="text-3xl font-bold mb-6">A Small Grain in the Ocean</h2>
          <p className="text-slate-300 mb-4">
            The idea worked as I expected. But I am not here to claim I am changing the world. CARTIEAI is just <span className="text-emerald-300">a small grain in the vast ocean</span> — a humble attempt to help <span className="text-white font-medium">finance and engineering teams</span> cut the waste, close the gap between them, and finally get real answers across every cloud and SaaS tool they use.
          </p>
          <p className="text-white font-medium text-lg">
            And honestly? That is enough for me.
          </p>
        </div>
      </section>

      {/* Wall of Love — testimonials auto-fill as they come in */}
      <WallOfLove />

      {/* What We Stand For */}
      <section className="py-12 px-4 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">What We Stand For</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-emerald-500/30 transition-colors text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center shadow-md shadow-emerald-500/25">
                <Sparkles className="w-6 h-6 text-emerald-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Simplicity First</h3>
              <p className="text-slate-400 text-sm">Cloud costs are complex enough. The tool to manage them should not be.</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-emerald-500/30 transition-colors text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center shadow-md shadow-cyan-500/25">
                <Shield className="w-6 h-6 text-emerald-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Your Data, Your Control</h3>
              <p className="text-slate-400 text-sm">Read-only access. Credentials never persisted. You can export or delete your data anytime — that's a promise.</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-rose-500/30 transition-colors text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-rose-500/30 to-pink-500/15 border border-rose-400/40 flex items-center justify-center shadow-md shadow-rose-500/25">
                <Heart className="w-6 h-6 text-rose-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Built with Heart</h3>
              <p className="text-slate-400 text-sm">Every feature exists because it genuinely helps someone.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The honest-FinOps moat — three things no other vendor will do */}
      <section className="py-14 px-4 relative overflow-hidden" data-testid="about-moat-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-amber-500/[0.04] blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] rounded-full bg-emerald-500/[0.04] blur-[100px]" />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <Badge className="bg-amber-500/15 border border-amber-500/30 text-amber-300 mb-4 text-xs font-mono tracking-widest px-3 py-1">WHAT MAKES US DIFFERENT</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              Three promises <span className="text-amber-600">no FinOps vendor before us</span> would make
            </h2>
            <p className="text-base text-slate-400 mt-4 max-w-2xl mx-auto leading-relaxed">
              Every other dashboard tells you what's wrong. We make ours <span className="text-white font-semibold">contractually binding</span>, <span className="text-white font-semibold">mathematically verifiable</span>, and <span className="text-white font-semibold">engineer-hour-aware</span>.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-950/30 to-slate-900/80 p-6">
              <div className="text-[10px] font-bold tracking-widest text-amber-400/80 uppercase mb-3">Our binding promise</div>
              <h3 className="text-lg font-bold text-white mb-2">3× ROI or full refund</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                If we don't return 3× your subscription in 90 days, you get a <span className="text-amber-300">full refund</span>. No email chains. No <span className="italic">"we tried our best."</span> A contract you can read before you pay.
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-950/30 to-slate-900/80 p-6">
              <div className="text-[10px] font-bold tracking-widest text-emerald-300/80 uppercase mb-3">Engineer-hour-aware math</div>
              <h3 className="text-lg font-bold text-white mb-2">Honest savings only</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Every recommendation is gated by <span className="text-emerald-300">$120/hr</span> engineer time. No more <span className="italic">"save $50 if you spend 8 hours."</span> Only payback-positive recs ship.
              </p>
            </div>
            <div className="rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-950/30 to-slate-900/80 p-6">
              <div className="text-[10px] font-bold tracking-widest text-emerald-400/80 uppercase mb-3">Verifiable, not "trust us"</div>
              <h3 className="text-lg font-bold text-white mb-2">Algorithm Correctness Manifest</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                <span className="text-emerald-300">Every algorithm</span> is proven by mathematical tests. <Link to="/algorithm-correctness" className="underline decoration-dotted hover:text-emerald-200">Verify the live manifest yourself</Link> in one curl — no signup, no NDA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Giving Back */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Handshake className="w-7 h-7 text-emerald-300" />
            </div>
            <h2 className="text-3xl font-bold mb-2">Giving Back</h2>
            <p className="text-emerald-300">Let's grow together</p>
          </div>
          
          <p className="text-slate-300 mb-6">
            I know what it feels like to be an early-stage startup — the excitement, the uncertainty, the late nights wondering if your idea will ever work.
          </p>
          <p className="text-slate-300 mb-6">
            I was there. I am still there in many ways.
          </p>
          <p className="text-slate-300 mb-8">
            That is why I want to encourage fellow dreamers, builders, and early-stage startups to reach out, collaborate, and share ideas. <span className="text-emerald-300">We are all in this together.</span>
          </p>

          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-emerald-300 mb-4">What we can offer:</h3>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-emerald-300">•</span>
                A listening ear for your ideas — sometimes you just need someone to bounce thoughts off
              </li>
              <li className="flex items-start gap-3">
                <span className="text-emerald-300">•</span>
                Collaboration opportunities — if our paths align, let us build something together
              </li>
              <li className="flex items-start gap-3">
                <span className="text-emerald-300">•</span>
                A helping hand in whatever little way I can
              </li>
              <li className="flex items-start gap-3">
                <span className="text-emerald-300">•</span>
                Share ideas — let us learn from each other and grow together
              </li>
            </ul>
            <p className="text-slate-400 mt-6 italic">
              I may not have much, but whatever little I can give — I will. Because someone once did the same for me.
            </p>
          </div>
        </div>
      </section>

      {/* Share this story */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/60 via-slate-900/30 to-slate-950/60 p-8 sm:p-10 overflow-hidden">
            {/* ambient glow */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />

            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                  <Sparkles className="w-3 h-3 text-emerald-300" />
                  <span className="text-[11px] font-semibold tracking-widest uppercase text-emerald-300">Help this story find its people</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
                  Share this story with someone who'd get it.
                </h2>
                <p className="text-sm text-slate-400 leading-relaxed max-w-lg">
                  If any part of this journey resonated — a founder you know, a FinOps teammate, a CFO who's tired of endless cost meetings — a single share goes further than you'd think.
                </p>
              </div>

              {/* Share buttons stack */}
              <div className="flex flex-col gap-2.5 w-full sm:w-auto sm:min-w-[200px]">
                <button
                  onClick={handleLinkedInShare}
                  className="group flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0A66C2] hover:bg-[#0957a8] text-slate-900 font-semibold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-emerald-500/20"
                  data-testid="share-linkedin-btn"
                >
                  <Linkedin className="w-4 h-4" />
                  Share on LinkedIn
                </button>
                <button
                  onClick={handleTwitterShare}
                  className="group flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-900/60 hover:bg-slate-800/60 text-white font-semibold text-sm transition-all hover:-translate-y-0.5 shadow-lg"
                  data-testid="share-twitter-btn"
                >
                  <Twitter className="w-4 h-4" />
                  Share on X
                </button>
                <button
                  onClick={handleCopyLink}
                  className={`group flex items-center justify-center gap-2 px-4 py-3 rounded-xl border font-semibold text-sm transition-all hover:-translate-y-0.5 ${
                    copied
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700'
                      : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/10 hover:border-white/20 text-slate-700'
                  }`}
                  data-testid="share-copy-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      Copy link + caption
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Preview of the pre-written post */}
            <div className="relative mt-8 pt-6 border-t border-white/5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Preview — we'll pre-fill this for you</p>
              <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
                <p className="text-sm text-slate-300 italic leading-relaxed">
                  "{shareText}"
                </p>
                <p className="text-xs text-emerald-300 mt-2">→ {shareUrl}</p>
              </div>
            </div>

            {/* Soft founder whisper — personal touch */}
            <div className="relative mt-6 flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
              <span className="text-base leading-none mt-0.5" aria-hidden>👋</span>
              <p>
                Hey — if you do share this, I'd genuinely love to say thank you.{' '}
                <a
                  href={FOUNDER_LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFounderDM}
                  className="text-emerald-700 hover:text-emerald-700 font-medium underline decoration-emerald-500/30 hover:decoration-emerald-400 underline-offset-4 transition-colors"
                  data-testid="founder-dm-link"
                >
                  DM me on LinkedIn
                </a>{' '}
                — I read every single one.
                <span className="flex items-center gap-2 mt-1.5">
                  <img
                    src="/images/founder-formal.jpg"
                    alt="Lakshmi Kiranmai Guduru"
                    className="w-6 h-6 rounded-full object-cover ring-2 ring-emerald-500/40"
                    loading="lazy"
                  />
                  <span className="text-xs text-slate-400">— Lakshmi Kiranmai Guduru, founder</span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Let's Connect */}
      <section className="py-12 px-4 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Mail className="w-7 h-7 text-emerald-300" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Let's Connect</h2>
          <p className="text-slate-400 mb-8">No pitch deck needed. Just tell me your story.</p>
          
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-8 border border-emerald-500/20">
            <h3 className="text-xl font-semibold text-white mb-2">Want to be part of this journey?</h3>
            <p className="text-slate-400 mb-6">We are just getting started. Come build with us.</p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/login">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-6">
                  Try CARTIEAI Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/careers">
                <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white px-6">
                  <Briefcase className="w-4 h-4 mr-2" />
                  View Careers
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-8 h-8" />
            <span className="text-xl font-bold">CARTIE<span className="text-emerald-300">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
          </div>
          <p className="text-slate-400 text-sm" data-testid="about-footer-entity">
            © 2026 <span className="text-slate-300">CARTIEAI LLC</span> · Registered in Texas, USA · All rights reserved.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Born in India. Rooted in Wichita. Home in Texas. · <a href="mailto:hello@cartieai.com" className="text-emerald-400 hover:text-emerald-300">hello@cartieai.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;
